import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import axiosInstance from '../../services/axiosInstance';

interface DevSnapshotProfile {
  id: string;
  label: string;
  kind: 'original' | 'prod-snapshot' | 'working-copy';
  mongoUri: string;
  dbName: string;
  uploadsDir: string;
  sourceDumpId?: string;
  importedAt?: string;
  verifiedAt?: string;
}

interface DevSnapshotStatus {
  enabled: boolean;
  activeProfile: DevSnapshotProfile | null;
  previousProfile: DevSnapshotProfile | null;
  runtimeProfile: DevSnapshotProfile | null;
  restartRequired: boolean;
  state: {
    activeProfileId: string;
    previousProfileId?: string;
    profiles: DevSnapshotProfile[];
    lastUpdatedAt: string;
  };
  config: {
    stateFilePath: string;
    snapshotsDir: string;
    prodUploadsDir: string;
    prodUploadsSourcePath: string;
    originalDbName: string;
    originalUploadsDir: string;
  };
}

interface DumpInfo {
  id: string;
  path: string;
  exists: boolean;
  collections: string[];
  requiredCollectionsPresent: boolean;
  modifiedAt: string | null;
  suggestedTargetDbName: string;
}

interface PreflightResult {
  ok: boolean;
  dumpId: string;
  dumpDir: string;
  targetDbName: string;
  targetMongoUri: string;
  mongorestorePath: string | null;
  mongorestoreError: string | null;
  checks: Record<string, any>;
}

interface FileVerificationSummary {
  assetsChecked: number;
  missingOriginals: number;
  thumbnailsChecked: number;
  missingThumbnails: number;
  mediumPreviewsChecked: number;
  missingMediumPreviews: number;
  missingOriginalSamples: Array<{ id: string; fileName?: string; filePath?: string }>;
  missingThumbnailSamples: Array<{ id: string; fileName?: string; expectedPath: string }>;
  missingMediumSamples: Array<{ id: string; fileName?: string; expectedPath: string }>;
}

interface ImportResult {
  message?: string;
  fileVerification?: FileVerificationSummary;
  rebaseSummary?: Record<string, number>;
  collectionCounts?: Record<string, number>;
}

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat('sv-SE', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const CheckLine: React.FC<{ label: string; ok: boolean; detail?: string }> = ({ label, ok, detail }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
    <Chip size="small" color={ok ? 'success' : 'error'} label={ok ? 'OK' : 'Fel'} />
    <Typography variant="body2">{label}</Typography>
    {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
  </Box>
);

const DevSnapshotsPage: React.FC = () => {
  const [status, setStatus] = useState<DevSnapshotStatus | null>(null);
  const [dumps, setDumps] = useState<DumpInfo[]>([]);
  const [selectedDumpId, setSelectedDumpId] = useState('');
  const [targetDbName, setTargetDbName] = useState('');
  const [preflight, setPreflight] = useState<PreflightResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, dumpsRes] = await Promise.all([
        axiosInstance.get<DevSnapshotStatus>('/api/dev-snapshots/status'),
        axiosInstance.get<{ dumps: DumpInfo[] }>('/api/dev-snapshots/dumps'),
      ]);
      setStatus(statusRes.data);
      setDumps(dumpsRes.data.dumps || []);
      const firstDump = dumpsRes.data.dumps?.[0];
      if (!selectedDumpId && firstDump) {
        setSelectedDumpId(firstDump.id);
        setTargetDbName(firstDump.suggestedTargetDbName);
      }
      setMessage(null);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Kunde inte ladda dev snapshots.' });
    } finally {
      setLoading(false);
    }
  }, [selectedDumpId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedDump = useMemo(
    () => dumps.find(dump => dump.id === selectedDumpId) || null,
    [dumps, selectedDumpId],
  );

  const handleDumpChange = (dumpId: string) => {
    setSelectedDumpId(dumpId);
    const dump = dumps.find(item => item.id === dumpId);
    if (dump) setTargetDbName(dump.suggestedTargetDbName);
    setPreflight(null);
    setImportResult(null);
  };

  const runPreflight = async () => {
    try {
      setActionLoading('preflight');
      setMessage(null);
      const { data } = await axiosInstance.post<PreflightResult>('/api/dev-snapshots/preflight', {
        dumpId: selectedDumpId,
        targetDbName,
      });
      setPreflight(data);
      setMessage({ type: data.ok ? 'success' : 'warning', text: data.ok ? 'Preflight OK.' : 'Preflight hittade problem.' });
    } catch (error: any) {
      const result = error.response?.data as PreflightResult | undefined;
      if (result?.checks) setPreflight(result);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Preflight misslyckades.' });
    } finally {
      setActionLoading(null);
    }
  };

  const importSnapshot = async () => {
    if (!window.confirm(`Importera snapshot till ny lokal databas "${targetDbName}"? Aktiv dev ändras inte förrän du aktiverar profilen.`)) {
      return;
    }
    try {
      setActionLoading('import');
      setMessage(null);
      const { data } = await axiosInstance.post('/api/dev-snapshots/import', {
        dumpId: selectedDumpId,
        targetDbName,
      });
      setImportResult(data);
      setMessage({ type: 'success', text: data.message || 'Snapshot importerad.' });
      await loadData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Import misslyckades.' });
    } finally {
      setActionLoading(null);
    }
  };

  const activateProfile = async (profileId: string) => {
    if (!window.confirm(`Aktivera profilen "${profileId}"? Backend måste startas om efteråt.`)) return;
    try {
      setActionLoading(`activate:${profileId}`);
      const { data } = await axiosInstance.post('/api/dev-snapshots/activate', { profileId });
      setMessage({ type: 'success', text: data.message || 'Profil aktiverad. Starta om backend.' });
      await loadData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Aktivering misslyckades.' });
    } finally {
      setActionLoading(null);
    }
  };

  const rollback = async () => {
    if (!window.confirm('Gå tillbaka till föregående profil? Backend måste startas om efteråt.')) return;
    try {
      setActionLoading('rollback');
      const { data } = await axiosInstance.post('/api/dev-snapshots/rollback');
      setMessage({ type: 'success', text: data.message || 'Rollback aktiverad. Starta om backend.' });
      await loadData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Rollback misslyckades.' });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading && !status) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography>Laddar dev snapshots...</Typography>
      </Box>
    );
  }

  if (!status?.enabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Dev Snapshots är inte aktiverat i backend.</Alert>
      </Box>
    );
  }

  const checks = preflight?.checks;
  const requiredCollections = checks?.requiredCollections || {};

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" gutterBottom>Dev Snapshots</Typography>
          <Typography variant="body2" color="text.secondary">
            Importera lokalt kopierade produktionsbackuper och växla mellan lokala dev-profiler. Produktion påverkas aldrig.
          </Typography>
        </Box>

        {message && <Alert severity={message.type}>{message.text}</Alert>}
        {status.restartRequired && (
          <Alert severity="warning">
            En annan profil är vald i lokal state än den backend kör med just nu. Starta om backend för att bytet ska börja gälla.
          </Alert>
        )}

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Aktiv profil</Typography>
            <Stack spacing={1}>
              <Typography><strong>State aktiv:</strong> {status.activeProfile?.label || '-'} ({status.activeProfile?.id || '-'})</Typography>
              <Typography><strong>Runtime aktiv:</strong> {status.runtimeProfile?.label || '-'} ({status.runtimeProfile?.id || '-'})</Typography>
              <Typography><strong>MongoDB:</strong> {status.runtimeProfile?.dbName || '-'}</Typography>
              <Typography><strong>Uploads:</strong> {status.runtimeProfile?.uploadsDir || '-'}</Typography>
              <Typography><strong>Rollback till:</strong> {status.previousProfile ? `${status.previousProfile.label} (${status.previousProfile.id})` : '-'}</Typography>
              <Box>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={actionLoading === 'rollback' ? <CircularProgress size={16} /> : <RestoreIcon />}
                  disabled={!status.previousProfile || Boolean(actionLoading)}
                  onClick={rollback}
                >
                  Gå tillbaka som det var innan
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Importera lokal prod-backup</Typography>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="dev-snapshot-dump-label">MongoDB-dump</InputLabel>
                <Select
                  labelId="dev-snapshot-dump-label"
                  label="MongoDB-dump"
                  value={selectedDumpId}
                  onChange={(event) => handleDumpChange(String(event.target.value))}
                >
                  {dumps.map(dump => (
                    <MenuItem key={dump.id} value={dump.id}>
                      {dump.id} {dump.requiredCollectionsPresent ? '' : '(saknar required collections)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedDump && (
                <Alert severity={selectedDump.requiredCollectionsPresent ? 'success' : 'warning'}>
                  Dump: {selectedDump.path} | Ändrad: {formatDate(selectedDump.modifiedAt)}
                </Alert>
              )}

              <TextField
                label="Ny lokal databas"
                value={targetDbName}
                onChange={(event) => {
                  setTargetDbName(event.target.value);
                  setPreflight(null);
                }}
                helperText="Importen får inte skriva till aktiv databas. Använd ett nytt namn."
                fullWidth
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={actionLoading === 'preflight' ? <CircularProgress size={16} /> : <FactCheckIcon />}
                  disabled={!selectedDumpId || !targetDbName || Boolean(actionLoading)}
                  onClick={runPreflight}
                >
                  Kör preflight
                </Button>
                <Button
                  variant="contained"
                  startIcon={actionLoading === 'import' ? <CircularProgress size={16} /> : <UploadFileIcon />}
                  disabled={!preflight?.ok || Boolean(actionLoading)}
                  onClick={importSnapshot}
                >
                  Importera snapshot
                </Button>
              </Stack>

              {preflight && (
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Preflight-resultat</Typography>
                  <CheckLine label="Dump vald" ok={Boolean(checks?.dumpIdProvided)} detail={preflight.dumpId} />
                  <CheckLine label="Dumpmapp finns" ok={Boolean(checks?.dumpDirectoryExists)} detail={preflight.dumpDir} />
                  <CheckLine label="assets.bson finns" ok={Boolean(requiredCollections.assets)} />
                  <CheckLine label="templates.bson finns" ok={Boolean(requiredCollections.templates)} />
                  <CheckLine label="categories.bson finns" ok={Boolean(requiredCollections.categories)} />
                  <CheckLine label="uploads-prod finns" ok={Boolean(checks?.uploadsProdExists)} detail={status.config.prodUploadsDir} />
                  <CheckLine label="Target DB-namn är giltigt" ok={Boolean(checks?.targetDbNameValid)} detail={preflight.targetDbName} />
                  <CheckLine label="Target DB är inte aktiv DB" ok={Boolean(checks?.targetDbIsNotActive)} />
                  <CheckLine label="Target DB finns inte redan" ok={Boolean(checks?.targetDbDoesNotExist)} />
                  <CheckLine label="uploads-prod är git-ignorerad" ok={Boolean(checks?.gitignore?.uploadsProdIgnored)} />
                  <CheckLine label="prod-snapshots är git-ignorerad" ok={Boolean(checks?.gitignore?.prodSnapshotsIgnored)} />
                  <CheckLine label="mongorestore hittades" ok={Boolean(checks?.mongorestoreAvailable)} detail={preflight.mongorestorePath || preflight.mongorestoreError || undefined} />
                </Box>
              )}

              {importResult?.fileVerification && (
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Importverifiering</Typography>
                  <CheckLine
                    label="Originalfiler finns"
                    ok={importResult.fileVerification.missingOriginals === 0}
                    detail={`${importResult.fileVerification.assetsChecked - importResult.fileVerification.missingOriginals}/${importResult.fileVerification.assetsChecked}`}
                  />
                  <CheckLine
                    label="Thumbnails finns"
                    ok={importResult.fileVerification.missingThumbnails === 0}
                    detail={`${importResult.fileVerification.thumbnailsChecked - importResult.fileVerification.missingThumbnails}/${importResult.fileVerification.thumbnailsChecked}`}
                  />
                  <CheckLine
                    label="Medium previews finns"
                    ok={importResult.fileVerification.missingMediumPreviews === 0}
                    detail={`${importResult.fileVerification.mediumPreviewsChecked - importResult.fileVerification.missingMediumPreviews}/${importResult.fileVerification.mediumPreviewsChecked}`}
                  />
                  {importResult.fileVerification.missingOriginalSamples.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Saknade originalfiler, exempel: {importResult.fileVerification.missingOriginalSamples.map(sample => sample.fileName || sample.id).join(', ')}
                    </Alert>
                  )}
                  {(importResult.fileVerification.missingThumbnails > 0 || importResult.fileVerification.missingMediumPreviews > 0) && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Saknade previews kan bero på att `uploads-prod` fortfarande kopieras eller att previews behöver byggas om.
                    </Alert>
                  )}
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Profiler</Typography>
            <Stack spacing={2} divider={<Divider flexItem />}>
              {status.state.profiles.map(profile => (
                <Box key={profile.id} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle1">
                      {profile.label} <Chip size="small" label={profile.kind} sx={{ ml: 1 }} />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">ID: {profile.id}</Typography>
                    <Typography variant="body2" color="text.secondary">DB: {profile.dbName}</Typography>
                    <Typography variant="body2" color="text.secondary">Uploads: {profile.uploadsDir}</Typography>
                    {profile.importedAt && <Typography variant="body2" color="text.secondary">Importerad: {formatDate(profile.importedAt)}</Typography>}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                      variant={status.activeProfile?.id === profile.id ? 'outlined' : 'contained'}
                      startIcon={actionLoading === `activate:${profile.id}` ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                      disabled={status.activeProfile?.id === profile.id || Boolean(actionLoading)}
                      onClick={() => activateProfile(profile.id)}
                    >
                      {status.activeProfile?.id === profile.id ? 'Vald i state' : 'Aktivera'}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default DevSnapshotsPage;
